import { useId, useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';

export interface ConfirmActionProps {
  readonly open: boolean;
  readonly title: string;
  readonly description?: ReactNode;
  /** 'destructive' turns the confirm button red and shows the irreversibility hint. */
  readonly severity?: 'default' | 'destructive';
  /**
   * When set, the user must type this exact phrase before confirming. Reserve it for the
   * genuinely irreversible — terminating a Group Policy, publishing an immutable version. Used on
   * routine actions it trains people to type without reading, which is worse than no guard.
   */
  readonly typedConfirmationPhrase?: string;
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  readonly busy?: boolean;
  /** Extra content between the description and the confirmation input — e.g. a dry-run preview. */
  readonly children?: ReactNode;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

/** The one sanctioned confirmation dialog, so "are you sure" looks and behaves the same everywhere. */
export function ConfirmAction({
  open,
  title,
  description,
  severity = 'default',
  typedConfirmationPhrase,
  confirmLabel,
  cancelLabel,
  busy = false,
  children,
  onConfirm,
  onCancel,
}: ConfirmActionProps): ReactNode {
  const { t } = useTranslation('patterns');
  const [typed, setTyped] = useState('');
  const titleId = useId();
  const descriptionId = useId();

  const destructive = severity === 'destructive';
  const phraseRequired = typedConfirmationPhrase !== undefined;
  const phraseMatches = !phraseRequired || typed.trim() === typedConfirmationPhrase;
  const showMismatch = phraseRequired && typed.length > 0 && !phraseMatches;

  const close = (): void => {
    setTyped('');
    onCancel();
  };

  return (
    <Dialog open={open} onClose={close} aria-labelledby={titleId} aria-describedby={descriptionId}>
      <DialogTitle id={titleId}>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {description !== undefined && (
            <DialogContentText id={descriptionId} variant="body2">
              {description}
            </DialogContentText>
          )}
          {children}
          {destructive && <Alert severity="warning">{t('confirm.destructiveHint')}</Alert>}
          {phraseRequired && (
            <TextField
              label={t('confirm.typedPhraseLabel')}
              helperText={
                showMismatch
                  ? t('confirm.mismatch', { phrase: typedConfirmationPhrase })
                  : t('confirm.typeToConfirm', { phrase: typedConfirmationPhrase })
              }
              error={showMismatch}
              value={typed}
              onChange={(event) => {
                setTyped(event.target.value);
              }}
              autoComplete="off"
              fullWidth
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>
          {cancelLabel ?? t('actions.cancel')}
        </Button>
        <Button
          variant="contained"
          color={destructive ? 'error' : 'primary'}
          onClick={onConfirm}
          disabled={busy || !phraseMatches}
        >
          {confirmLabel ?? t('actions.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
